/**
 * AI folder organization (WHA-3654).
 *
 * Mirrors public.folder_organize_requests and the proposal JSON written by
 * the backend organizer agent. The agent only proposes; moves happen when
 * the user confirms and apply_organize_proposal() runs server-side.
 */

export type OrganizeScope = "loose" | "subtree";

/**
 * How a proposal was produced. "agent" is the GPT-5.6 run; "tags" is the
 * deterministic grouping computed in SQL from the documents' own tags.
 * Mirrors folder_organize_requests.mode.
 */
export type OrganizeMode = "agent" | "tags";

/** Tag dimensions the deterministic mode can nest by, in the user's order. */
export type OrganizeDimension = "category" | "discipline" | "stage";

export const ORGANIZE_DIMENSIONS: readonly OrganizeDimension[] = [
  "category",
  "discipline",
  "stage",
];

/** The i18n key under documents.tagFilter naming each dimension. */
export const DIMENSION_LABEL_KEYS: Record<OrganizeDimension, string> = {
  category: "categories",
  discipline: "designDisciplines",
  stage: "designStages",
};

/**
 * Server-side cap on a tags proposal (request_tag_organization rejects past
 * it). Mirrored here so the launch screen can explain the limit BEFORE the
 * user commits, rather than surfacing it as a failed request.
 */
export const TAG_MODE_MAX_FOLDERS = 200;

/** Folder-name rules apply_organize_proposal re-validates server-side. */
export const MAX_FOLDER_NAME_LENGTH = 100;

export type OrganizeStatus =
  | "pending"
  | "running"
  | "proposal_ready"
  | "applying"
  | "applied"
  | "failed"
  | "cancelled"
  | "expired";

export interface ProposalFolder {
  key: string;
  name: string;
  parent_key: string | null;
  /** Set when the agent reuses an existing subfolder instead of creating one. */
  existing_folder_id: number | null;
}

export interface ProposalAssignment {
  document_id: number;
  source_folder_id: number;
  action: "move" | "keep";
  /** Present when action === "move". */
  target_key?: string;
  reason: string;
}

export interface OrganizeProposal {
  version: number;
  scope: OrganizeScope;
  source_folder_id: number;
  folders: ProposalFolder[];
  assignments: ProposalAssignment[];
  summary: string;
}

export interface ProposalTreeNode {
  folder: ProposalFolder;
  depth: number;
  children: ProposalTreeNode[];
}

/**
 * The proposed structure as a tree, which is the shape the review UI draws.
 * The proposal is LLM-generated, so a folder's key may repeat or its
 * parent_key may dangle/form a cycle — the shared visited set makes walk()
 * terminate, and folders unreachable from the root are dropped entirely.
 */
export function proposalTree(proposal: OrganizeProposal | null): ProposalTreeNode[] {
  if (!proposal) return [];
  const byParent = new Map<string | null, ProposalFolder[]>();
  for (const f of proposal.folders) {
    const list = byParent.get(f.parent_key) ?? [];
    list.push(f);
    byParent.set(f.parent_key, list);
  }
  const visited = new Set<string>();
  const walk = (parentKey: string | null, depth: number): ProposalTreeNode[] => {
    const nodes: ProposalTreeNode[] = [];
    for (const folder of byParent.get(parentKey) ?? []) {
      if (visited.has(folder.key)) continue;
      visited.add(folder.key);
      nodes.push({ folder, depth, children: walk(folder.key, depth + 1) });
    }
    return nodes;
  };
  return walk(null, 0);
}

/**
 * The same folders, flattened parent-before-child with depth. Derived from
 * proposalTree() rather than walking again, so the set the tree renders and
 * the set reviewableMoves() validates against can never drift apart.
 */
export function reachableFolders(
  proposal: OrganizeProposal
): Array<{ folder: ProposalFolder; depth: number }> {
  const out: Array<{ folder: ProposalFolder; depth: number }> = [];
  const flatten = (nodes: ProposalTreeNode[]) => {
    for (const node of nodes) {
      out.push({ folder: node.folder, depth: node.depth });
      flatten(node.children);
    }
  };
  flatten(proposalTree(proposal));
  return out;
}

/**
 * Moves the review UI can actually render: action "move" with a target_key
 * that resolves to a proposed folder reachable from the tree root (an
 * unreachable folder is never rendered, so a move into it could never be
 * unchecked), for a document with exactly ONE move assignment and NO "keep"
 * assignment. A document the agent assigned to two folders — or both moved
 * and kept — is ambiguous: exclusion is per document_id, so we couldn't show
 * one outcome while the server applies another; such documents are dropped
 * from the moves, the dialog excludes them at apply time, and
 * keptDocumentCount() reports them among the files that stay put.
 * Single source of truth for what is shown, counted, AND applied — a
 * malformed assignment can never be applied invisibly.
 */
export function reviewableMoves(proposal: OrganizeProposal | null): ProposalAssignment[] {
  if (!proposal) return [];
  const folderKeys = new Set(reachableFolders(proposal).map(({ folder }) => folder.key));
  const moveCounts = new Map<number, number>();
  const keptIds = new Set<number>();
  for (const a of proposal.assignments) {
    if (a.action === "move") {
      moveCounts.set(a.document_id, (moveCounts.get(a.document_id) ?? 0) + 1);
    } else if (a.action === "keep") {
      keptIds.add(a.document_id);
    }
  }
  return proposal.assignments.filter(
    (a) =>
      a.action === "move" &&
      !!a.target_key &&
      folderKeys.has(a.target_key) &&
      moveCounts.get(a.document_id) === 1 &&
      !keptIds.has(a.document_id)
  );
}

/**
 * The reviewable moves bucketed by the folder they land in. Keyed by the
 * proposal's folder keys, so every bucket has a row in proposalTree() to be
 * unchecked on — a move whose target the tree does not render is dropped by
 * reviewableMoves() and so never appears here either.
 */
export function movesByFolderKey(
  proposal: OrganizeProposal | null
): Map<string, ProposalAssignment[]> {
  const byKey = new Map<string, ProposalAssignment[]>();
  for (const a of reviewableMoves(proposal)) {
    if (!a.target_key) continue;
    const list = byKey.get(a.target_key) ?? [];
    list.push(a);
    byKey.set(a.target_key, list);
  }
  return byKey;
}

/**
 * Documents in the proposal that will NOT move: explicit keeps plus any
 * document whose move assignments reviewableMoves() dropped as malformed or
 * ambiguous (those are excluded server-side at apply time, so they stay put
 * too). Counting the complement of the reviewable moves keeps the review
 * totals reconciled with the full proposal — no document vanishes from both
 * the move list and the "stay where they are" summary.
 */
export function keptDocumentCount(proposal: OrganizeProposal | null): number {
  if (!proposal) return 0;
  const moving = new Set(reviewableMoves(proposal).map((a) => a.document_id));
  return new Set(proposal.assignments.map((a) => a.document_id).filter((id) => !moving.has(id)))
    .size;
}

export interface OrganizeApplyResult {
  status: "applied" | "expired" | "failed";
  moved?: number;
  /** Invisible container children (e.g. DWG child PDFs) carried along with their parent. */
  children_moved?: number;
  skipped_stale?: number;
  excluded?: number;
  kept?: number;
  folders_created?: number;
  error?: string;
}

export interface FolderOrganizeRequest {
  id: number;
  org_id: string;
  building_id: number;
  project_id: number;
  /** Null when the request targets the project root (loose files). */
  folder_id: number | null;
  scope: OrganizeScope;
  /** How the proposal was made. Older rows predate the column and read as agent. */
  mode: OrganizeMode | null;
  /** Tags mode only: the dimensions nested by, in order. */
  dimensions: OrganizeDimension[] | null;
  /** Tags mode only: the UI language the folder names were generated in. */
  language: string | null;
  /** Free-text user guidance forwarded into the agent's prompt. */
  instructions: string | null;
  status: OrganizeStatus;
  proposal: OrganizeProposal | null;
  apply_result: OrganizeApplyResult | null;
  error: string | null;
  created_at: string;
  completed_at: string | null;
  expires_at: string | null;
}

/** Statuses with an organize run still in flight or awaiting the user. */
export const ACTIVE_ORGANIZE_STATUSES: readonly OrganizeStatus[] = [
  "pending",
  "running",
  "proposal_ready",
  "applying",
];

/**
 * Statuses the dialog resumes into when (re)opened: everything active, plus
 * failed/expired so the user sees the outcome (and error) of the last run
 * instead of silently landing on the launch screen.
 */
export const RESUMABLE_ORGANIZE_STATUSES: readonly OrganizeStatus[] = [
  ...ACTIVE_ORGANIZE_STATUSES,
  "failed",
  "expired",
];

/**
 * Why the server refused to start a run because another one already covers
 * the same documents. Runs lock only the documents they cover: a folder, or
 * with "whole folder tree" everything below it too — so a refusal names which
 * way the overlap runs.
 */
export type OrganizeStartConflict =
  | "sameFolder"
  | "wholeProject"
  | "parentFolder"
  | "subfolder"
  | "folderInProject";

/**
 * Classify a request RPC's 23505 refusal by its message — the exact strings
 * private.organize_request_conflict raises (franklin-data-pipeline), so change
 * them together. Anything else returns null and is shown verbatim.
 */
export function organizeStartConflict(message: string): OrganizeStartConflict | null {
  if (message.includes("covering the whole project")) return "wholeProject";
  if (message.includes("for a parent folder")) return "parentFolder";
  if (message.includes("for a subfolder")) return "subfolder";
  if (message.includes("of this project")) return "folderInProject";
  // Also the form an older database raises for ANY overlap ("for a parent or
  // subfolder of this folder"), until the pipeline migration applies.
  if (message.includes("already in progress")) return "sameFolder";
  return null;
}

export interface TagProposalCounts {
  documents: number;
  folders: number;
  moves: number;
  kept: number;
  /** Documents carrying more than one tag on a chosen dimension. */
  ambiguous: number;
}

/**
 * A document as the review modal draws it. Deliberately the fields
 * `DocumentTableRow` and `ViewerDocumentData` need and no more: the modal shows
 * the Documents page's row (icon, size, when) and opens the same viewer, so it
 * needs the file's path and the `state` that `canPreviewDocumentRow` reads to
 * decide whether a preview is even possible.
 */
export interface ProposalDocument {
  id: number;
  filename: string;
  file_path: string;
  building_id: number;
  content_type: string | null;
  size: number | null;
  state: string | null;
  created_at: string;
  document_content_modified_date: string | null;
  original_created_at: string | null;
  version: number | null;
  parent_document_id: number | null;
}

/** What preview_tag_organization returns: the proposal plus its size. */
export interface TagProposalPreview {
  proposal: OrganizeProposal;
  counts: TagProposalCounts;
}

/**
 * A folder path with the middle elided, keeping the first and last segments
 * always visible — the project and the folder actually being organised are
 * the two the reader needs. Mirrors FolderBreadcrumb's collapse rule.
 *
 * Returns the segments to render plus whether anything was dropped, so the
 * caller can render the ellipsis itself and put the full path in a title.
 */
export function elideFolderPath(
  segments: string[],
  maxVisible = 4
): { head: string[]; elided: string[]; tail: string[] } {
  const clamped = Math.max(maxVisible, 2);
  if (segments.length <= clamped) return { head: segments, elided: [], tail: [] };
  return {
    head: segments.slice(0, 1),
    elided: segments.slice(1, -(clamped - 1)),
    tail: segments.slice(-(clamped - 1)),
  };
}

/**
 * The chain of folder names from the top of the project down to `folderId`,
 * inclusive. Empty when the folder is not in the tree.
 *
 * Walks the nested tree rather than reading document_folders.folder_path:
 * legacy folders have that column null, which is exactly why
 * collectSubtreeFolderIds walks parent links too. Structurally typed so it
 * does not drag TreeFolder (and its whole module) into this file.
 */
export function folderAncestorPath<T extends { id: string; name: string; children?: T[] }>(
  tree: T[],
  folderId: number | null
): string[] {
  if (folderId === null) return [];
  const target = String(folderId);
  const seek = (nodes: T[], trail: string[]): string[] | null => {
    for (const node of nodes) {
      const next = [...trail, node.name];
      if (node.id === target) return next;
      const found = node.children?.length ? seek(node.children, next) : null;
      if (found) return found;
    }
    return null;
  };
  return seek(tree, []) ?? [];
}

/**
 * Whether a reviewer's folder rename can be applied. Mirrors the rules
 * apply_organize_proposal re-validates, so the modal can refuse a name in
 * place instead of failing at apply time. Sibling collisions are checked by
 * the caller, which knows the tree.
 */
export function folderNameError(name: string): "empty" | "tooLong" | "slash" | null {
  const trimmed = name.trim();
  if (!trimmed) return "empty";
  if (trimmed.length > MAX_FOLDER_NAME_LENGTH) return "tooLong";
  if (trimmed.includes("/")) return "slash";
  return null;
}

/**
 * Whether a scope can actually be run, given the counts.
 *
 * "Whole folder tree" on a folder with no subfolders is not a choice the user
 * can ever unlock, so the dialog omits it rather than showing it greyed out.
 * Counts not loaded yet (undefined) read as available, so options do not pop
 * in and out while the query resolves.
 */
export function isScopeAvailable(
  scope: OrganizeScope,
  counts: OrganizeScopeCounts | undefined
): boolean {
  if (!counts) return true;
  return scope === "loose" ? counts.looseDocuments > 0 : counts.subfolders > 0;
}

export interface OrganizeScopeCounts {
  looseDocuments: number;
  subtreeDocuments: number;
  subfolders: number;
}
